/** @format */

import {useState} from 'react';

import {useTranslation} from 'react-i18next';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';

import {VerifyCode as VerifyCodeScreen} from 'ui';
import {useAuthContext} from 'contexts/AuthContext';
import {secondLevelDomain} from 'helpers/routerHelpers';

import {useVerifyCodeMutation} from './graphql/__generated__';

const VerifyCode = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = useAuthContext();
  const {t} = useTranslation();

  const [error, setError] = useState<string>();
  const [verifyCode, {loading}] = useVerifyCodeMutation();

  const handleSubmit = async (code: string) => {
    setError(undefined);
    try {
      const {data} = await verifyCode({variables: {code}});
      if (!data) {
        // No data + no throw (GraphQL partial-errors) — give feedback instead
        // of silently leaving the entered code sitting there.
        setError(
          t(
            'Не удалось выполнить вход, попробуйте ещё раз',
            'Не удалось выполнить вход, попробуйте ещё раз',
          ),
        );
        return;
      }
      await authContext.signIn(data.verifyCode);
      const from = location.state?.from ?? {pathname: '/grouping'};
      navigate(from, {replace: true});
    } catch (e) {
      if (e instanceof Error) setError(e.message);
    }
  };

  // verify-code is only a legitimate destination mid-2FA — SignIn sets this
  // flag when it forks here. A direct hit (fresh tab, refresh, shared link)
  // has no pending session, so bounce to sign-in instead of showing a dead
  // code screen. This also guarantees sign-in sits behind us in history, so
  // the Back button's navigate(-1) is always safe.
  if (!location.state?.pending2fa) {
    return <Navigate to='/auth/sign-in' replace />;
  }

  return (
    <VerifyCodeScreen
      submitState={loading ? 'submitting' : 'idle'}
      error={error}
      onSubmit={handleSubmit}
      onBackClick={() => navigate(-1)}
      onLogoClick={() => window.location.assign(`https://${secondLevelDomain()}/`)}
    />
  );
};

export default VerifyCode;
